import type { Character } from '../domain/character.ts';
import type { SessionState } from '../domain/session.ts';
import type { RuntimeState } from '../domain/runtime.ts';
import type { Skill } from '../content/types.ts';
import type { ContentRegistry } from '../content/registry.ts';
import type { Roller } from '../rules/dice.ts';
import type { RollMode } from '../rules/rollModes.ts';
import type { Effect } from '../rules/effects.ts';
import type { AttackPlan } from './attackService.ts';
import type { AttackCheckOutcome } from '../rules/attackResolution.ts';
import {
  attackAgainstFor,
  attackCooldownState,
  payAttackCost,
  planAttackFor,
  startAttackCooldown,
} from './attackService.ts';
import { buildEffectiveCharacter } from './effectiveCharacter.ts';
import { markSkillUsed, markSkillExplicitTrigger, markHandToHandDamageEffect, handToHandAdvancementProblem } from './advancementService.ts';
import { attackHistoryEntry } from './historyEntries.ts';
import { addRoll } from '../rules/rollHistory.ts';
import { inEncounter } from '../domain/runtime.ts';
import type { WeaponSwapPlan } from '../rules/weaponSwap.ts';
import { planWeaponSwap } from '../rules/weaponSwap.ts';
import { encounterActionProblem, resolveTrigger } from '../rules/encounter.ts';
import { spendEncounterAction } from './encounterService.ts';
import { isSpell, spellCastActionCost } from '../rules/spellCast.ts';
import { recordUntrainedAttempt } from '../rules/sessionAdvancement.ts';

/**
 * The Attack Action, as one application pathway.
 *
 * Attacking used to be assembled inside `PlayScreen.handleAttack`: it rolled,
 * spent Mana, marked the Skill and saved the character, and never touched the
 * Action pool at all. Spending an Action was a separate button the player was
 * trusted to press. That was survivable while the interface was entirely
 * manual, and it stops being survivable the moment anything is priced "as part
 * of the Attack Action" - there was no Action for it to be part of.
 *
 * ONE Attack changes THREE durable records:
 *
 *   - Character: Mana spent on the cast, and the advancement mark;
 *   - Session:   the Attack roll in the history;
 *   - Runtime:   the Action spent, expired conditions, and any cooldown.
 *
 * They are produced here as pure values and committed together by the caller
 * through `CheckpointCommitter`. This service writes nothing itself.
 *
 * ORDER MATTERS, and it is the whole reason this is a service rather than a
 * handful of calls in a component. See `resolveAttackAction` below.
 */

export interface AttackActionInput {
  character: Character;
  session: SessionState;
  /**
   * The runtime BEFORE the Action is spent, or null outside a fight.
   *
   * Null is a legitimate, supported state: see the out-of-encounter policy on
   * `resolveAttackAction`.
   */
  runtime: RuntimeState | null;
  skill: Skill;
  mode: RollMode;
  useAiming?: boolean;
  /** Mark this actually-used Damage Effect instead of the hand-to-hand Attack. */
  advancementDamageEffectId?: string | null;
  targetEvade: number | null;
  effects: readonly Effect[];
  registry: ContentRegistry;
  now: string;
  newId: () => string;
  /**
   * RULE DCC-HOT-011 - swap to this Hotlist weapon first, FREE, as part of this
   * Attack. Omitted or null when attacking with what is already in hand.
   *
   * Rules p.48: "If a crawler swaps their currently equipped weapon to another
   * weapon on their Hotlist, they swap for free as part of the Attack Action."
   * C&C p.15: "You only spend one Action on this swap and attack."
   *
   * It rides on the Attack's single Action and adds nothing to the cost. The
   * Action is still spent in exactly one place, at exactly the same point in
   * the sequence.
   */
  swapFromHotlistSlot?: number | null;
}

export type AttackActionOutcome =
  | {
      ok: true;
      character: Character;
      session: SessionState;
      runtime: RuntimeState | null;
      plan: AttackPlan;
      aimingRank: number;
      outcome: AttackCheckOutcome;
      /** Ended by the Check itself, then by the Action ending. In that order. */
      expiredEffectIds: string[];
      /** True when an encounter Action was actually spent. */
      actionSpent: boolean;
      /** The free weapon swap this Attack performed, or null. RULE DCC-HOT-011. */
      swap: WeaponSwapPlan | null;
    }
  | { ok: false; reason: string };

/**
 * Whether the crawler can afford the Action this Attack will cost.
 *
 * PREFLIGHT ONLY. It spends an Action on a COPY of the runtime purely to ask
 * the question, and that copy is thrown away - deliberately. Adopting it would
 * be wrong twice over: `spendEncounterAction` also resolves `after-next-action`,
 * so the Effects that are supposed to modify THIS Attack would already have
 * expired before the dice were rolled, and Staggered would never once apply to
 * the Attack it is printed to hinder.
 *
 * Outside an encounter there is no Action pool, so there is nothing to afford.
 */
function actionAffordable(
  runtime: RuntimeState | null,
  actorId: string,
  count: number,
): string | null {
  // RULE DCC-ENC-003 - phase AND count, through the shared authority.
  //
  // This used to call `spendAction` on a throwaway runtime and throw the result
  // away, which answered "can they afford it" and nothing about WHEN. An Attack
  // during Mob Resolution was affordable and illegal. It also meant the Attack
  // pathway carried its own copy of a rule four other services need.
  //
  // A weapon Attack and an Attack Spell are both ORDINARY Actions. An Attack
  // Spell does not become an Interrupt because some Spell somewhere has the
  // trait.
  return encounterActionProblem(runtime, actorId, count, 'normal');
}

/**
 * Resolves one Attack Action completely, as pure values.
 *
 * THE ORDER, and why each step is where it is:
 *
 *  1. **Preflight the cooldown and the Action**, before any dice, any Mana and
 *     any record changes. A refusal must cost the player nothing at all - not a
 *     Mana point, not a mark, not a cooldown, and not a roll in their history.
 *  2. **Build the EffectiveCharacter from the PRE-Action Effects.** Staggered
 *     grants Disadvantage on the next Attack and Queasy on the next Action
 *     requiring a roll; both end when that Action ends. If the Action were
 *     spent first they would be gone before they applied.
 *  3. **Roll the Attack Check** against that view.
 *  4. **Fire `after-next-check`.** RULE DCC-COND-005: Stunned lasts "Once you
 *     make a Check" (Rules p.46). An Attack Check IS a Check. Ordinary Checks
 *     have always fired this through `performRoll`; Attack Checks went through
 *     `attackAgainstFor` directly and never did, so Stunned applied
 *     Disadvantage and then stayed. Firing it here fixes that hole on the one
 *     pathway every Attack now takes.
 *  5. **Start the cooldown**, which is a consequence of having attacked.
 *  6. **Finish the Action** through `spendEncounterAction`, the shared pathway,
 *     which decrements it and fires `after-next-action`.
 *
 * Nothing here names a condition. Steps 4 and 6 are the existing trigger
 * mechanism; which Effects respond is content, and Stunned, Staggered and
 * Queasy behave correctly because their printed durations are already modelled.
 *
 * OUT-OF-ENCOUNTER POLICY, chosen deliberately rather than by accident. The
 * books describe Actions as a structure of the combat round (Rules p.19, p.34);
 * nothing says a crawler may not swing at something outside one, and the
 * application has always allowed it. So an Attack with no encounter resolves
 * normally and decrements nothing - there is no pool to decrement. Cooldowns
 * are likewise only started inside an encounter, so an out-of-combat Attack
 * cannot leave a permanent "Once per round" block behind it.
 */
export function resolveAttackAction(roller: Roller, input: AttackActionInput): AttackActionOutcome {
  const fighting = input.runtime !== null && inEncounter(input.runtime);

  // --- 0. The free weapon swap, BEFORE anything else. RULE DCC-HOT-011. ----
  //
  // First because the Attack is made with the weapon that ends up in hand: the
  // plan, the Mana cost, the cooldown and the dice all have to be worked out
  // from the swapped-in weapon, not the one being put away. Rules p.40 has Park
  // swap to his Dagger and roll the Dagger's attack in the same breath.
  //
  // It is PURE and costs nothing. If it refuses, nothing has changed and no
  // Action has been spent - the whole point of a preflight-then-commit shape.
  let subject = input.character;
  let swap: WeaponSwapPlan | null = null;
  const requestedSwap = input.swapFromHotlistSlot ?? null;
  if (requestedSwap !== null) {
    const swapped = planWeaponSwap(subject, requestedSwap, {
      lookupItem: (id) => input.registry.getItem(id),
      lookupAttack: (id) => input.registry.getAttack(id),
    });
    if (!swapped.ok) return { ok: false, reason: swapped.reason };

    // THE SWAP DECIDES THE ATTACK. C&C p.15: "the weapon you're wielding moves
    // into the Hotlist while the new weapon moves out, and you roll an attack
    // WITH THE NEW WEAPON." So a requested swap is not a free draw followed by
    // an unrelated Attack - the weapon drawn IS the weapon swung, and Rules
    // p.40 shows it in one breath: Park swaps to his Dagger and rolls the
    // Dagger's attack.
    //
    // Refused HERE, before Mana, cooldown, dice, marks, history and the Action,
    // so a mismatch costs nothing and changes nothing.
    //
    // NARROW ON PURPOSE. This says nothing about an ordinary Attack made with
    // no swap, which still does not check that a matching weapon is in hand -
    // that is a genuinely separate and still-open question, and closing it
    // would change every Attack in the game. See `OPEN:` in the tests.
    if (input.skill.id !== swapped.plan.incomingAttackId) {
      const drawn = input.registry.getItem(swapped.plan.incomingItemId)?.name ?? 'that weapon';
      return {
        ok: false,
        reason:
          `Drawing ${drawn} means attacking with it. ${input.skill.name} is a different Attack, ` +
          'so choose that Attack without a swap, or swap to the weapon it belongs to (C&C p.15).',
      };
    }

    subject = swapped.character;
    swap = swapped.plan;
  }

  const plan = planAttackFor(
    buildEffectiveCharacter(subject, input.effects, (id) => input.registry.getItem(id)),
    input.skill,
    input.registry,
  );
  if (!plan) return { ok: false, reason: `${input.skill.name} is not an Attack.` };

  // --- 1. Preflight. Nothing has changed yet and nothing may. -------------
  const cooldown = attackCooldownState(plan, input.runtime);
  if (cooldown.blocked) return { ok: false, reason: cooldown.reason as string };

  // RULE DCC-SPELL-002 - DERIVED FROM THE CRAWLER, before the preflight and
  // used by both it and the spend, so the two can never disagree. A weapon
  // Attack is one Action; a Spell is one when validly prepared in the Hotlist
  // and two when merely known. Which control the player pressed is not an
  // input: a Hotlisted Fireball costs one Action whether it was cast from its
  // slot or picked out of this panel.
  const actionCost = isSpell(input.skill)
    ? spellCastActionCost(input.character, input.skill.id, {
        lookupItem: (id) => input.registry.getItem(id),
        lookupSkill: (id) => input.registry.getSkill(id),
      })
    : 1;

  const unaffordable = actionAffordable(input.runtime, input.character.id, actionCost);
  if (unaffordable !== null) return { ok: false, reason: unaffordable };

  // Mana is spent on the CAST and is not refunded on a miss (C&C p.34), so it
  // is paid after the preflight and before the roll.
  const paid = payAttackCost(subject, plan);
  if (!paid.ok) return { ok: false, reason: paid.reason };

  // --- 2 & 3. The crawler as they are RIGHT NOW, then the Check. ----------
  const effective = buildEffectiveCharacter(paid.character, input.effects, (id) =>
    input.registry.getItem(id),
  );
  const aimingRank = input.useAiming ? effective.effectiveSkillRank('aiming') : 0;
  if (input.useAiming && (input.skill.kind !== 'ranged-attack' || effective.baseSkillRank('aiming') < 1)) return { ok: false, reason: 'Aiming requires its own trained Ranks and a Ranged Attack.' };
  const damageEffect = input.advancementDamageEffectId ? input.registry.getSkill(input.advancementDamageEffectId) : null;
  const choiceProblem = input.advancementDamageEffectId ? handToHandAdvancementProblem(paid.character, input.skill, plan.profile, input.advancementDamageEffectId, input.registry) : null;
  if (choiceProblem) return { ok: false, reason: choiceProblem };
  const attackMode = input.useAiming ? 'disadvantage' : input.mode;
  const outcome = attackAgainstFor(roller, effective, plan, attackMode, input.targetEvade, aimingRank > 0 ? [{ label: 'Aiming bonus', value: aimingRank }] : []);

  let character = damageEffect ? markHandToHandDamageEffect(paid.character, damageEffect.id, input.registry) : markSkillUsed(paid.character, input.skill.id, input.registry);
  if (input.useAiming) character = markSkillExplicitTrigger(character, 'aiming', input.registry);
  let session: SessionState = {
    ...input.session,
    rollHistory: addRoll(
      input.session.rollHistory,
      attackHistoryEntry({
        id: input.newId(),
        timestamp: input.now,
        plan,
        outcome,
        effective,
        manualMode: attackMode,
      }),
    ),
  };
  if (outcome.check.untrained) {
    session = recordUntrainedAttempt(session, input.skill.id, paid.character.floor);
  }

  // --- 4. The Check has been made. ---------------------------------------
  const expiredEffectIds: string[] = [];
  let runtime = input.runtime;
  if (runtime) {
    const afterCheck = resolveTrigger(runtime, 'after-next-check');
    runtime = afterCheck.runtime;
    expiredEffectIds.push(...afterCheck.expiredEffectIds);
  }

  // --- 5 & 6. Cooldown, then the Action ends. ----------------------------
  if (runtime && fighting) {
    runtime = startAttackCooldown(plan, runtime);

    const spent = spendEncounterAction(runtime, input.character.id, actionCost, 'normal');
    if (!spent.ok) {
      // The preflight already proved this affordable against the same runtime,
      // so reaching here means an assumption broke rather than a player doing
      // something ordinary. Refusing whole is still the right answer: nothing
      // has been written, so nothing is half-applied.
      return { ok: false, reason: spent.reason };
    }
    runtime = spent.runtime;
    expiredEffectIds.push(...spent.expiredEffectIds);
  }

  return {
    ok: true,
    character,
    session,
    runtime,
    plan,
    aimingRank,
    outcome,
    // The swap happened as part of THIS Attack and cost nothing extra. Reported
    // so the sheet can say what was drawn and what went away.
    swap,
    expiredEffectIds,
    actionSpent: fighting,
  };
}
